import {FC, useContext} from "react";
import {makeStyles} from "tss-react/mui";
import {Accordion, AccordionDetails, AccordionSummary, Box, FormControl, InputLabel, MenuItem, OutlinedInput, Select, Stack, Typography} from "@mui/material";
import {ICourseEntity, ICourseEntityPayload} from "@buildwitt/shapeshift-types";
import {AdminEntityPageContainer, ConfirmDeleteEntityDialog, AdminEntityPageContainerProps} from "@buildwitt/shapeshift-common/v2/admin"
import {cohortCourseValidationDoc} from "../../../validationDocs/user/cohortCourseValidationDoc";
import {AdminHeader} from "@/pages/ManagerView/ContentCreation/AdminHeader";
import {AdminFormContainer} from "@/pages/ManagerView/ContentCreation/AdminFormContainer";
import {Link} from "react-router-dom";
import {IconContext, useApi} from "@buildwitt/shapeshift-identity";
import {AdminInputText} from "@/pages/ManagerView/ContentCreation/AdminInputText";
import {AdminInputSelect} from "@/pages/ManagerView/ContentCreation/AdminInputSelect";
import {IconAdminCourses} from "@/icons/IconAdminCourses";
import {AdminInputSwitch} from "@/pages/ManagerView/ContentCreation/AdminInputSwitch";
import ImageSelector from "@/pages/ManagerView/ContentCreation/ImageSelector";
import AdminCourseCurriculum from "./AdminCourseCurriculumV2/CourseCurriculum";
import {jobCategories, workCategories} from "@/constants";

const useStyles = makeStyles()((theme) => ({
	formFieldset: {
		borderRadius: "4px",
		"&.Mui-expanded": {
			margin: 0
		}
	},
	formFieldsetSummary: {
		padding: theme.spacing(0, 3, 0.5),
		".MuiAccordionSummary-content": {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1)
		}
	},
	divider: {
		borderTop: `1px solid ${theme.palette.grey[200]}`,
		width: "100%",
		height: "1px"
	},
	subtitle: {
		opacity: 0.7
	},
	formFieldsetDetails: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(4),
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(3)
	},
	fieldsetSettings: {
		paddingTop: theme.spacing(3),
		paddingBottom: theme.spacing(6)
	},
	formFieldsetNumber: {
		lineHeight: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "24px",
		marginRight: theme.spacing(2),
		color: theme.palette.primary.contrastText,
		borderRadius: "100%",
		aspectRatio: "1 / 1",
		backgroundColor: theme.palette.primary.main
	},
	expandIcon: {},
	fileInput: {
		"[class*=imgContainer]": {
			marginTop: theme.spacing(2)
		},
		"&>div": {
			position: "relative",
			"&>.MuiTypography-root": {
				position: "absolute",
				margin: 0,
				top: "-12px",
				left: "10px",
				backgroundColor: theme.palette.background.paper,
				padding: theme.spacing(0.5),
				fontSize: "12px"
			}
		}
	}
}));

interface IProps extends Partial<AdminEntityPageContainerProps<ICourseEntity, "course">> {
	courseId?: number,
	className?: string
}

const template: ICourseEntityPayload = {
	title: "", slug: "", content: "", description: "", curriculum: [],
	featuredImage: null
}

const contentTypes: string[] = ["Job Training", "Safety", "Personal Development", "Professional Development"]

export const AdminCoursePageContent: FC<IProps> = ({courseId, ...props}) => {
	const entityId = courseId;
	const {classes, cx} = useStyles();
	const {icon} = useContext(IconContext);

	const ICourseEntityToICourseEntityPayload = (e: ICourseEntity): ICourseEntityPayload => {
		const {title, slug, content, id, createdAt, updatedAt, privacy, description, linearProgression} = e
		const featuredImage = e.featuredImage?.id
		const contentType = (e as any).contentType;
		const jobCategory = e.recommendedWeighting?.find(e => e.attribute === "jobCategory")?.value.split(",").map(e => e.trim()) || [];
		const workCategory = e.recommendedWeighting?.find(e => e.attribute === "workCategory")?.value.split(",").map(e => e.trim()) || [];
		const payload = {
			title, slug, content, id, createdAt, updatedAt, privacy, description, contentType, featuredImage, linearProgression, jobCategory, workCategory
		}
		return payload;
	}

	return (
		<AdminEntityPageContainer<ICourseEntity & ICourseEntityPayload & {enrollmentCount?: number, workCategory: string[]}, "course">
			entityId={entityId}
			entityToPayload={ICourseEntityToICourseEntityPayload}
			template={template}
			validationDoc={cohortCourseValidationDoc}
			type="course"
			componentOverrides={{
				adminHeader: (props) =>
					<AdminHeader rootSlug="/admin/courses" rootBreadcrumb={<><IconAdminCourses /><Link to={"/admin/courses"}><Typography variant="body1">Courses</Typography></Link></>} editMode={courseId ? "edit" : "create"} {...props} />,
				adminFormContainer: ({entity, ...containerProps}) =>
					<AdminFormContainer {...containerProps} entity={entity}
						rootSlug="/admin/courses" componentOverrides={{deleteDialog: deleteProps => <ConfirmDeleteEntityDialog {...deleteProps} componentOverrides={{content:  entity?.enrollmentCount && entity?.enrollmentCount >= 1 ? <Typography variant="body1">You will be making an assigned course unavailable.</Typography> : <Typography variant="body1">Do you want to delete?</Typography>}}/>}}/>
			}}
			{...props}
		>
			{({formData, setFormData, entity, locale, files, setFiles, validation}) => {
			
				return <>
					<Accordion defaultExpanded className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>1</span><Typography variant="h6" component="p">Course Information</Typography></Stack>
							<Typography className={classes.subtitle}>Add the basic details for your course in English.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<AdminInputText error={validation?.[0]?.title} required label="Title" onChange={e => setFormData(prev => ({...prev, title: e.target.value}))} value={formData.title} />
							<AdminInputText error={validation?.[0]?.description} multiline rows={5} label="Description" onChange={e => setFormData(prev => ({...prev, description: e.target.value}))} value={formData.description} />
						</AccordionDetails>
					</Accordion>

					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>2</span><Typography variant="h6" component="p">Course Details</Typography></Stack>
							<Typography className={classes.subtitle}>Choose a category and upload a thumbnail to represent your course.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
								{locale === "en" && <AdminInputSelect<string> error={(validation?.[0] as any)?.contentType} required label="Content Type" onChange={e => setFormData(prev => ({...prev, contentType: e.target.value}))} options={contentTypes.map(e => ({label: e, value: e}))} value={(formData as any).contentType?.toString() ?? ""}/>}
								{(locale === "en" && (formData as any).contentType?.toString() === "Job Training") && <>
									<FormControl sx={{minWidth: "200px"}}>
										<InputLabel shrink id="work-cat-select-label">Work Category</InputLabel>
										<Select
											labelId="work-cat-select-label"
											id="work-cat-select"
											label="Work Category"
											multiple
											value={formData?.workCategory ?? []}
											onChange={e => {
												const { target: { value } } = e;
												setFormData(prev => ({...prev, workCategory: typeof value === 'string' ? value.split(',') : value}));
											}}
											input={<OutlinedInput notched label="Work Category" />}
											>
											{workCategories.map((cat) => (
												<MenuItem
												key={cat}
												value={cat}
												// style={getStyles(cat, personName, theme)}
												>
												{cat}
												</MenuItem>
											))}
											</Select>
									</FormControl>
								</>}
								{(locale === "en" && (formData as any).contentType?.toString() === "Job Training") && <>
									<FormControl sx={{minWidth: "200px"}}>
										<InputLabel shrink id="job-cat-select-label">Job Category</InputLabel>
										<Select
											labelId="job-cat-select-label"
											id="job-cat-select"
											label="Job Category"
											multiple
											value={formData?.jobCategory ?? []}
											onChange={e => {
												const { target: { value } } = e;
												setFormData(prev => ({...prev, jobCategory: typeof value === 'string' ? value.split(',') : value}));
											}}
											input={<OutlinedInput notched label="Job Category" />}
											>
											{jobCategories.map((cat) => (
												<MenuItem
												key={cat}
												value={cat}
												// style={getStyles(cat, personName, theme)}
												>
												{cat}
												</MenuItem>
											))}
											</Select>
									</FormControl>
								</>}
							</Box>
							{locale === "en" && <ImageSelector label="Featured Image" error={validation?.[0]?.featuredImage} defaultImageUrl={entity?.featuredImage?.url} onImageSelected={(files) => {
								setFiles(prev => ({...prev, featuredImage: files ?? null}));
								if (!files || files.length === 0) {
									setFormData(prev => ({...prev, featuredImage: null}));
								}
							}}/>}
						</AccordionDetails>
					</Accordion>

					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>3</span><Typography variant="h6" component="p">Add Lessons</Typography></Stack>
							<Typography className={classes.subtitle}>Start building your course by adding new content or importing existing lessons. You can include up to 20 lessons.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							{locale === "en" && <AdminCourseCurriculum initialData={entity} onChange={e => setFormData(prev => ({...prev, curriculum: e}))}/>}
						</AccordionDetails>
					</Accordion>

					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={icon("expandMoreIcon", {
								className: classes.expandIcon
							})}
						>
							<Stack direction="row" alignItems="center"><span className={classes.formFieldsetNumber}>4</span><Typography variant="h6" component="p">Course Settings</Typography></Stack>
							<Typography className={classes.subtitle}>Customize how this course is shared, assigned, and interacted with.</Typography>
						</AccordionSummary>
						<AccordionDetails className={cx(classes.formFieldsetDetails, classes.fieldsetSettings)}>
							{locale === "en" && <><div className={classes.divider}/><AdminInputSwitch error={validation?.[0]?.privacy} label="Share across other companies" onChange={(e) => setFormData(prev => ({...prev, privacy: e.target.checked ? "global" : "cohort"}))} checked={formData.privacy === "global"} /></>}

							{locale === "en" && <><div className={classes.divider}/><AdminInputSwitch error={validation?.[0]?.linearProgression} label="Lock next lesson until previous is done" onChange={() => setFormData(prev => ({...prev, linearProgression: !prev.linearProgression}))} checked={formData.linearProgression} /></>}
						</AccordionDetails>
					</Accordion>
				</>}}
		</AdminEntityPageContainer>
	);
}
